import { computed, onUnmounted, watch } from 'vue';
import { useEventListener } from '@vueuse/core';
import { useAccount } from './useAccount';
import { useStore } from './store';
import ConversationAPI from 'dashboard/api/conversations';
import { updateFaviconBadge } from 'dashboard/helper/AudioAlerts/faviconHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';

const REFRESH_INTERVAL_MS = 30000;
const REFRESH_DELAY_MS = 300;
const MIN_REQUEST_INTERVAL_MS = 2000;
const READ_ACTIONS = new Set(['markMessagesRead', 'markMessagesUnread']);
const REFRESH_ACTIONS = new Set([
  ...READ_ACTIONS,
  'addMessage',
  'updateMessage',
  'addConversation',
  'updateConversation',
  'deleteConversation',
  'deleteMessage',
  'assignAgent',
  'toggleStatus',
]);

// One writer for the title and every favicon, independent of audio and sidebar flags.
export function useTabUnreadBadge() {
  const store = useStore();
  const { accountId } = useAccount();
  const userId = computed(() => store.getters.getCurrentUser?.id);
  const channel =
    typeof BroadcastChannel === 'undefined'
      ? null
      : new BroadcastChannel('chatwoot-tab-unread');
  let revision = 0;
  let timer = null;
  let inFlight = false;
  let needsRefresh = false;
  let stopped = false;
  let lastRequestAt = 0;

  const getRefreshDelay = () =>
    Math.max(
      REFRESH_DELAY_MS,
      MIN_REQUEST_INTERVAL_MS - (Date.now() - lastRequestAt)
    );

  const refresh = async () => {
    timer = null;
    if (stopped || !accountId.value || !userId.value) return;
    const requestRevision = revision;
    const requestAccountId = accountId.value;
    const requestUserId = userId.value;
    needsRefresh = false;
    inFlight = true;
    lastRequestAt = Date.now();
    try {
      const { data } =
        await ConversationAPI.getTabUnreadCount(requestAccountId);
      if (
        !stopped &&
        requestRevision === revision &&
        requestAccountId === accountId.value &&
        requestUserId === userId.value
      ) {
        updateFaviconBadge(data.count);
      }
    } catch {
      // Preserve the last confirmed count during a network failure; resync on reconnect.
    } finally {
      inFlight = false;
      if (needsRefresh && !stopped) {
        timer = setTimeout(refresh, getRefreshDelay());
      }
    }
  };

  const scheduleRefresh = () => {
    revision += 1;
    needsRefresh = true;
    if (stopped || timer || inFlight) return;
    timer = setTimeout(refresh, getRefreshDelay());
  };

  watch(
    [accountId, userId],
    () => {
      updateFaviconBadge(0);
      scheduleRefresh();
    },
    { immediate: true, flush: 'sync' }
  );

  const unsubscribe = store.subscribeAction({
    after: ({ type }) => {
      if (!REFRESH_ACTIONS.has(type)) return;
      scheduleRefresh();
      if (READ_ACTIONS.has(type)) {
        // Send only an invalidation, never a possibly stale count or customer data.
        channel?.postMessage({
          accountId: accountId.value,
          userId: userId.value,
        });
      }
    },
  });
  if (channel) {
    channel.onmessage = ({ data }) => {
      if (data.accountId === accountId.value && data.userId === userId.value) {
        scheduleRefresh();
      }
    };
  }

  useEventListener(window, 'focus', scheduleRefresh);
  useEventListener(window, 'online', scheduleRefresh);
  useEventListener(document, 'visibilitychange', () => {
    if (!document.hidden) scheduleRefresh();
  });
  emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, scheduleRefresh);
  const interval = setInterval(() => {
    // Reconcile missed events, reads on another device and background tab suspension.
    if (!inFlight) scheduleRefresh();
  }, REFRESH_INTERVAL_MS);

  onUnmounted(() => {
    stopped = true;
    clearTimeout(timer);
    clearInterval(interval);
    unsubscribe();
    channel?.close();
    emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT, scheduleRefresh);
    updateFaviconBadge(0);
  });
}
